// ─── Theme Colors ────────────────────────────────────────────────────────────

export const COLORS = {
  modalBg: '#1a1b22',
  cardBg: '#22242c',
  sectionBg: 'rgba(255, 255, 255, 0.025)',
  rowBg: 'rgba(255, 255, 255, 0.02)',
  rowHoverBg: 'rgba(255, 255, 255, 0.055)',
  elevatedBg: 'rgba(255, 255, 255, 0.04)',

  primary: '#F52740',
  primaryHover: '#d01347',
  primaryMuted: 'rgba(245, 39, 64, 0.12)',
  primaryGlow: '0 0 12px rgba(245, 39, 64, 0.3)',

  teal: '#278d94',
  tealMuted: 'rgba(39, 141, 148, 0.15)',

  win: '#42c079',
  winBg: 'rgba(66, 192, 121, 0.07)',
  winText: '#7ddda5',
  winBorder: 'rgba(66, 192, 121, 0.4)',
  loss: '#f52740',
  lossBg: 'rgba(245, 39, 64, 0.07)',
  lossBorder: 'rgba(245, 39, 64, 0.4)',

  running: '#278d94',
  runningBg: 'rgba(39, 141, 148, 0.12)',
  cancelled: 'rgba(255, 255, 255, 0.5)',
  cancelledBg: 'rgba(255, 255, 255, 0.06)',
  completed: '#42c079',
  completedBg: 'rgba(66, 192, 121, 0.08)',

  textPrimary: '#ffffff',
  textSecondary: '#d1d1d2',
  textMuted: '#a3b1bf',
  textDisabled: '#8a8a92',

  border: 'rgba(67, 80, 95, 0.5)',
  borderLight: 'rgba(255, 255, 255, 0.06)',
  gradientDivider: 'linear-gradient(to right, rgba(56, 57, 67, 0) 0%, rgba(67, 80, 95, 0.8) 20%, rgba(67, 80, 95, 0.8) 80%, rgba(56, 57, 67, 0) 100%)',

  cardSuit: {
    hearts: '#e74c3c',
    diamonds: '#3498db',
    clubs: '#2ecc71',
    spades: '#f1c40f',
  },
} as const;

// ─── Types ───────────────────────────────────────────────────────────────────

export type HistoryTab = 'cash' | 'mtt';
export type CashFilter = 'all' | 'texas' | 'aof';
export type MttFilter = 'all' | 'completed' | 'cancelled' | 'running';
export type CashResultSelection = 'ALL' | 'WIN' | 'LOSS' | 'PUSH';
export type TournamentResultSelection = 'ALL' | 'CASHED' | 'UNCASHED' | 'CANCELLED';
export type CurrencyTypeSelection = 'ANY' | 'FIAT' | 'CRYPTO';
export type TriStateSelection = 'ANY' | 'TRUE' | 'FALSE';
export type TournamentGameTypeSelection = 'ANY' | 'TEXAS' | 'AOF';

export type CashFilterDraft = {
  gameType: CashFilter;
  roomNameContains: string;
  result: CashResultSelection;
  minNetAmount: string;
  maxNetAmount: string;
  startTimeFrom: string;
  startTimeTo: string;
  minSmallBlind: string;
  maxSmallBlind: string;
  minBigBlind: string;
  maxBigBlind: string;
  minAnte: string;
  maxAnte: string;
  currency: string;
  currencyType: CurrencyTypeSelection;
};

export type MttFilterDraft = {
  status: MttFilter;
  nameContains: string;
  result: TournamentResultSelection;
  startTimeFrom: string;
  startTimeTo: string;
  minBuyIn: string;
  maxBuyIn: string;
  minEntryFee: string;
  maxEntryFee: string;
  minGuaranteedPrizePool: string;
  maxGuaranteedPrizePool: string;
  minLateRegistrationMinutes: string;
  maxLateRegistrationMinutes: string;
  minRegisteredPlayers: string;
  maxRegisteredPlayers: string;
  minMaxPlayers: string;
  maxMaxPlayers: string;
  isSatellite: TriStateSelection;
  gameType: TournamentGameTypeSelection;
  rebuyEnabled: TriStateSelection;
  addonEnabled: TriStateSelection;
  currency: string;
  currencyType: CurrencyTypeSelection;
};

export type SortDirection = 'asc' | 'desc';
export type SortState<T extends string> = { column: T; direction: SortDirection };
export type CashSortColumn = 'room' | 'type' | 'blinds' | 'pot' | 'date' | 'pl';
export type MttSortColumn = 'name' | 'status' | 'pos' | 'buyin' | 'date' | 'prize';
export type MttHandSortColumn = 'hand' | 'table' | 'stack' | 'date' | 'time';

export type TournamentSelection = {
  tournamentGameId: string;
  name?: string;
  startTime?: string | number;
  totalHandCount?: number;
};

// ─── Page Sizes & Filter Defaults ────────────────────────────────────────────

export const CASH_PAGE_SIZE = 30;
export const MTT_PAGE_SIZE = 30;
export const MTT_HANDS_PAGE_SIZE = 50;

export const CASH_FILTER_DEFAULT: CashFilterDraft = {
  gameType: 'all',
  roomNameContains: '',
  result: 'ALL',
  minNetAmount: '',
  maxNetAmount: '',
  startTimeFrom: '',
  startTimeTo: '',
  minSmallBlind: '',
  maxSmallBlind: '',
  minBigBlind: '',
  maxBigBlind: '',
  minAnte: '',
  maxAnte: '',
  currency: '',
  currencyType: 'ANY',
};

export const MTT_FILTER_DEFAULT: MttFilterDraft = {
  status: 'all',
  nameContains: '',
  result: 'ALL',
  startTimeFrom: '',
  startTimeTo: '',
  minBuyIn: '',
  maxBuyIn: '',
  minEntryFee: '',
  maxEntryFee: '',
  minGuaranteedPrizePool: '',
  maxGuaranteedPrizePool: '',
  minLateRegistrationMinutes: '',
  maxLateRegistrationMinutes: '',
  minRegisteredPlayers: '',
  maxRegisteredPlayers: '',
  minMaxPlayers: '',
  maxMaxPlayers: '',
  isSatellite: 'ANY',
  gameType: 'ANY',
  rebuyEnabled: 'ANY',
  addonEnabled: 'ANY',
  currency: '',
  currencyType: 'ANY',
};

// ─── Shared Styles ───────────────────────────────────────────────────────────

export const MONO_FONT = '"SF Mono", monospace';

export const scrollContainerSx = {
  flex: 1,
  overflowY: 'auto' as const,
  overflowX: 'hidden' as const,
  pr: 0.5,
  scrollbarWidth: 'thin' as const,
  scrollbarColor: 'rgba(245, 39, 64, 0.4) transparent',
  '&::-webkit-scrollbar': { width: '5px' },
  '&::-webkit-scrollbar-track': { background: 'transparent' },
  '&::-webkit-scrollbar-thumb': {
    background: 'rgba(245, 39, 64, 0.4)',
    borderRadius: '4px',
    '&:hover': { background: 'rgba(245, 39, 64, 0.6)' },
  },
};

export const tableHeaderSx = {
  display: 'grid',
  gap: 1,
  px: 1.5,
  py: 1,
  borderBottom: `1px solid ${COLORS.borderLight}`,
  position: 'sticky' as const,
  top: 0,
  bgcolor: COLORS.modalBg,
  zIndex: 1,
};

export const tableRowSx = (index: number, clickable = true) => ({
  display: 'grid',
  gap: 1,
  alignItems: 'center',
  px: 1.5,
  py: 0.85,
  cursor: clickable ? 'pointer' : 'default',
  borderRadius: '4px',
  bgcolor: index % 2 === 0 ? 'transparent' : 'rgba(255, 255, 255, 0.015)',
  transition: 'background-color 0.1s ease',
  '&:hover': { bgcolor: COLORS.rowHoverBg },
});

export const detailRowSx = (index: number) => ({
  display: 'grid',
  gap: 1,
  alignItems: 'center',
  px: 1,
  py: 0.5,
  borderRadius: '3px',
  bgcolor: index % 2 === 0 ? 'transparent' : 'rgba(255, 255, 255, 0.015)',
  '&:hover': { bgcolor: COLORS.rowHoverBg },
});

// ─── Sort Helper ─────────────────────────────────────────────────────────────

export const toggleSortColumn = <T extends string>(
  prev: SortState<T>,
  col: T,
): SortState<T> =>
  prev.column === col
    ? { column: col, direction: prev.direction === 'desc' ? 'asc' : 'desc' }
    : { column: col, direction: 'desc' };
